import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import WhatsAppConfig from "@/components/WhatsAppConfig";
import RazorpayConfig from "@/components/RazorpayConfig";
import RegistrationsTab from "@/components/RegistrationsTab";
import CouponsTab from "@/components/CouponsTab";
import ActivitiesTab from "@/components/ActivitiesTab";
import {
  LogOut, Upload, Plus, Trash2, Save, Users, IndianRupee, Ticket, Loader2, Image as ImageIcon, Download, Handshake, Trophy, ChevronLeft, ChevronRight,
} from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [regs, setRegs] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingEvent, setUploadingEvent] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileRef = useRef();
  const eventFileRef = useRef();
  const logoFileRef = useRef();

  const loadAll = () => {
    api.get("/content").then((r) => setContent(r.data));
    api.get("/registrations").then((r) => setRegs(r.data)).catch(() => {});
    api.get("/sponsors").then((r) => setSponsors(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  };

  useEffect(() => { loadAll(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const body = patch || content;
      const { data } = await api.put("/content", body);
      setContent(data);
      toast.success("Changes saved");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const uploadHero = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/content/hero-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setContent((c) => ({ ...c, hero_image: data.hero_image }));
      toast.success("Hero image updated");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const uploadEventImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingEvent(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/content/event-images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setContent((c) => ({ ...c, event_images: data.event_images }));
      toast.success("Image added");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploadingEvent(false);
      if (eventFileRef.current) eventFileRef.current.value = "";
    }
  };

  const deleteEventImage = async (idx) => {
    const next = (content.event_images || []).filter((_, i) => i !== idx);
    setContent((c) => ({ ...c, event_images: next }));
    await save({ ...content, event_images: next });
  };

  const moveEventImage = async (idx, dir) => {
    const arr = [...(content.event_images || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setContent((c) => ({ ...c, event_images: arr }));
    await save({ ...content, event_images: arr });
  };

  const uploadMarqueeLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/content/marquee-logos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setContent((c) => ({ ...c, marquee_logos: data.marquee_logos }));
      toast.success("Logo added");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploadingLogo(false);
      if (logoFileRef.current) logoFileRef.current.value = "";
    }
  };

  const deleteMarqueeLogo = async (idx) => {
    const next = (content.marquee_logos || []).filter((_, i) => i !== idx);
    setContent((c) => ({ ...c, marquee_logos: next }));
    await save({ ...content, marquee_logos: next });
  };

  const moveMarqueeLogo = async (idx, dir) => {
    const arr = [...(content.marquee_logos || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setContent((c) => ({ ...c, marquee_logos: arr }));
    await save({ ...content, marquee_logos: arr });
  };

  const [uploadingPartner, setUploadingPartner] = useState(-1);
  const partnerFileRef = useRef();
  const partnerUploadIdx = useRef(-1);

  const addCommunityPartner = () => {
    const next = [...(content.community_partners || []), { logo: "", name: "" }];
    setContent((c) => ({ ...c, community_partners: next }));
  };

  const updateCommunityPartner = (idx, patch) => {
    const arr = [...(content.community_partners || [])];
    arr[idx] = { ...arr[idx], ...patch };
    setContent((c) => ({ ...c, community_partners: arr }));
  };

  const saveCommunityPartners = async (arr) => {
    const list = arr || content.community_partners || [];
    await save({ ...content, community_partners: list });
  };

  const removeCommunityPartner = async (idx) => {
    const next = (content.community_partners || []).filter((_, i) => i !== idx);
    setContent((c) => ({ ...c, community_partners: next }));
    await save({ ...content, community_partners: next });
  };

  const pickPartnerLogo = (idx) => {
    partnerUploadIdx.current = idx;
    partnerFileRef.current?.click();
  };

  const uploadCommunityPartnerLogo = async (e) => {
    const file = e.target.files?.[0];
    const idx = partnerUploadIdx.current;
    if (!file || idx < 0) return;
    setUploadingPartner(idx);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/content/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const arr = [...(content.community_partners || [])];
      arr[idx] = { ...arr[idx], logo: data.url };
      setContent((c) => ({ ...c, community_partners: arr }));
      await save({ ...content, community_partners: arr });
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploadingPartner(-1);
      partnerUploadIdx.current = -1;
      if (partnerFileRef.current) partnerFileRef.current.value = "";
    }
  };

  if (!content) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-[#A1A1AA]">Loading…</div>;

  const set = (updater) => setContent((c) => ({ ...c, ...updater(c) }));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-border bg-[#121212] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-display text-xl font-900 uppercase tracking-tight">
            Meraki Moto <span className="text-[#FF4500]">Mania</span> <span className="text-[#71717A] text-sm font-500 normal-case tracking-normal">/ Admin</span>
          </span>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#A1A1AA] hidden sm:inline">{user?.email}</span>
            <Button data-testid="logout-button" onClick={doLogout} variant="outline" size="sm" className="rounded-full border-border">
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Ticket} label="Total Registrations" value={stats?.total ?? "—"} />
          <StatCard icon={Users} label="Paid" value={stats?.paid ?? "—"} />
          <StatCard icon={Ticket} label="Meraki / Day (paid)" value={stats ? `${stats.meraki_paid} / ${stats.day_paid}` : "—"} />
          <StatCard icon={IndianRupee} label="Revenue" value={stats ? `₹${stats.revenue.toLocaleString("en-IN")}` : "—"} />
        </div>

        <Tabs defaultValue="hero">
          <TabsList className="bg-[#121212] border border-border flex-wrap h-auto">
            <TabsTrigger value="hero" data-testid="tab-hero">Hero</TabsTrigger>
            <TabsTrigger value="event" data-testid="tab-event">Event Details</TabsTrigger>
            <TabsTrigger value="day" data-testid="tab-day">Day Pass</TabsTrigger>
            <TabsTrigger value="meraki" data-testid="tab-meraki">Meraki Pass</TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="tab-vehicles">Vehicles</TabsTrigger>
            <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
            <TabsTrigger value="regs" data-testid="tab-regs">Registrations</TabsTrigger>
            <TabsTrigger value="coupons" data-testid="tab-coupons">Coupons</TabsTrigger>
            <TabsTrigger value="activities" data-testid="tab-activities">Activities</TabsTrigger>
            <TabsTrigger value="sponsors" data-testid="tab-sponsors">Sponsors</TabsTrigger>
            <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">WhatsApp</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          </TabsList>

          {/* HERO */}
          <TabsContent value="hero">
            <Panel title="Hero Section">
              <div className="rounded-xl overflow-hidden border border-border mb-4 max-w-2xl">
                <img src={mediaUrl(content.hero_image)} alt="hero" className="w-full h-56 object-cover" data-testid="admin-hero-preview" />
              </div>
              <div className="flex flex-wrap gap-3 items-end">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadHero} data-testid="hero-file-input" />
                <Button data-testid="hero-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full bg-[#FF4500] hover:bg-[#FF5722]">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />} Upload Image
                </Button>
                <div className="flex-1 min-w-[240px]">
                  <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">Or paste image URL</Label>
                  <div className="flex gap-2 mt-1">
                    <Input data-testid="hero-url-input" value={content.hero_image} onChange={(e) => set(() => ({ hero_image: e.target.value }))} placeholder="https://…" />
                    <Button data-testid="hero-url-save" onClick={() => save()} variant="outline" className="rounded-full border-border"><ImageIcon className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            </Panel>
          </TabsContent>

          {/* EVENT */}
          <TabsContent value="event">
            <Panel title="Event Details" onSave={() => save()} saving={saving}>
              <div className="grid md:grid-cols-2 gap-4">
                <TextField label="Event Name" testid="ev-name" value={content.event.name} onChange={(v) => set((c) => ({ event: { ...c.event, name: v } }))} />
                <TextField label="Tagline" testid="ev-tagline" value={content.event.tagline} onChange={(v) => set((c) => ({ event: { ...c.event, tagline: v } }))} />
                <TextField label="Date" testid="ev-date" value={content.event.date} onChange={(v) => set((c) => ({ event: { ...c.event, date: v } }))} />
                <TextField label="Time" testid="ev-time" value={content.event.time} onChange={(v) => set((c) => ({ event: { ...c.event, time: v } }))} />
                <TextField label="Venue" testid="ev-venue" value={content.event.venue} onChange={(v) => set((c) => ({ event: { ...c.event, venue: v } }))} className="md:col-span-2" />
              </div>
              <div className="mt-4 space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">Description</Label>
                <Textarea data-testid="ev-description" rows={4} value={content.event.description} onChange={(e) => set((c) => ({ event: { ...c.event, description: e.target.value } }))} />
              </div>
              <ListEditor
                label="Highlights"
                testid="highlights"
                items={content.event.highlights || []}
                onChange={(arr) => set((c) => ({ event: { ...c.event, highlights: arr } }))}
              />
              <div className="mt-6 border-t border-border pt-5">
                <div className="flex items-center justify-between mb-3 gap-3">
                  <div>
                    <Label className="text-sm font-semibold">Event Details Images</Label>
                    <p className="text-xs text-[#71717A] mt-0.5">Uploaded posters appear one-by-one in a slider on the home page. Use arrows to reorder.</p>
                  </div>
                  <input ref={eventFileRef} type="file" accept="image/*" className="hidden" onChange={uploadEventImage} data-testid="event-image-input" />
                  <Button data-testid="event-image-upload" onClick={() => eventFileRef.current?.click()} disabled={uploadingEvent} className="rounded-full bg-[#FF4500] hover:bg-[#FF5722] shrink-0">
                    {uploadingEvent ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />} Add Image
                  </Button>
                </div>
                {(content.event_images || []).length === 0 ? (
                  <p className="text-sm text-[#71717A]" data-testid="event-images-empty">No images yet. Add posters to show them on the home page.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="event-images-grid">
                    {(content.event_images || []).map((img, idx) => (
                      <div key={idx} className="relative rounded-lg overflow-hidden border border-border bg-black" data-testid={`event-image-${idx}`}>
                        <img src={mediaUrl(img)} alt={`event ${idx + 1}`} className="w-full h-40 object-contain bg-black" />
                        <div className="absolute top-1 left-1 text-[10px] bg-black/70 rounded px-1.5 py-0.5">#{idx + 1}</div>
                        <div className="absolute inset-x-0 bottom-0 flex justify-between p-1 bg-gradient-to-t from-black/80 to-transparent">
                          <div className="flex gap-1">
                            <button onClick={() => moveEventImage(idx, -1)} data-testid={`event-image-${idx}-left`} className="h-7 w-7 rounded bg-white/10 hover:bg-white/20 flex items-center justify-center"><ChevronLeft className="h-4 w-4" /></button>
                            <button onClick={() => moveEventImage(idx, 1)} data-testid={`event-image-${idx}-right`} className="h-7 w-7 rounded bg-white/10 hover:bg-white/20 flex items-center justify-center"><ChevronRight className="h-4 w-4" /></button>
                          </div>
                          <button onClick={() => deleteEventImage(idx)} data-testid={`event-image-${idx}-delete`} className="h-7 w-7 rounded bg-red-500/80 hover:bg-red-500 flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6 border-t border-border pt-5">
                <div className="flex items-center justify-between mb-3 gap-3">
                  <div>
                    <Label className="text-sm font-semibold">Logo Marquee</Label>
                    <p className="text-xs text-[#71717A] mt-0.5">Partner/sponsor logos scroll across the home page. Displayed at a fixed size (160 × 80) — upload transparent PNGs for best results. Use arrows to reorder.</p>
                  </div>
                  <input ref={logoFileRef} type="file" accept="image/*" className="hidden" onChange={uploadMarqueeLogo} data-testid="marquee-logo-input" />
                  <Button data-testid="marquee-logo-upload" onClick={() => logoFileRef.current?.click()} disabled={uploadingLogo} className="rounded-full bg-[#FF4500] hover:bg-[#FF5722] shrink-0">
                    {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />} Add Logo
                  </Button>
                </div>
                {(content.marquee_logos || []).length === 0 ? (
                  <p className="text-sm text-[#71717A]" data-testid="marquee-logos-empty">No logos yet. Add partner logos to show a scrolling marquee on the home page.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="marquee-logos-grid">
                    {(content.marquee_logos || []).map((img, idx) => (
                      <div key={idx} className="relative rounded-lg overflow-hidden border border-border bg-[#1a1a1a]" data-testid={`marquee-logo-${idx}`}>
                        <img src={mediaUrl(img)} alt={`logo ${idx + 1}`} className="w-full h-24 object-contain p-3" />
                        <div className="absolute top-1 left-1 text-[10px] bg-black/70 rounded px-1.5 py-0.5">#{idx + 1}</div>
                        <div className="absolute inset-x-0 bottom-0 flex justify-between p-1 bg-gradient-to-t from-black/80 to-transparent">
                          <div className="flex gap-1">
                            <button onClick={() => moveMarqueeLogo(idx, -1)} data-testid={`marquee-logo-${idx}-left`} className="h-7 w-7 rounded bg-white/10 hover:bg-white/20 flex items-center justify-center"><ChevronLeft className="h-4 w-4" /></button>
                            <button onClick={() => moveMarqueeLogo(idx, 1)} data-testid={`marquee-logo-${idx}-right`} className="h-7 w-7 rounded bg-white/10 hover:bg-white/20 flex items-center justify-center"><ChevronRight className="h-4 w-4" /></button>
                          </div>
                          <button onClick={() => deleteMarqueeLogo(idx)} data-testid={`marquee-logo-${idx}-delete`} className="h-7 w-7 rounded bg-red-500/80 hover:bg-red-500 flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6 border-t border-border pt-5">
                <input ref={partnerFileRef} type="file" accept="image/*" className="hidden" onChange={uploadCommunityPartnerLogo} data-testid="community-partner-input" />
                <div className="flex items-center justify-between mb-3 gap-3">
                  <div>
                    <Label className="text-sm font-semibold">Riding Community Partners</Label>
                    <p className="text-xs text-[#71717A] mt-0.5">Each partner shows a logo with its name below it on the home page. Add a logo and type the name.</p>
                  </div>
                  <Button data-testid="community-partner-add" onClick={addCommunityPartner} className="rounded-full bg-[#FF4500] hover:bg-[#FF5722] shrink-0">
                    <Plus className="h-4 w-4 mr-1" /> Add Partner
                  </Button>
                </div>
                {(content.community_partners || []).length === 0 ? (
                  <p className="text-sm text-[#71717A]" data-testid="community-partners-empty">No community partners yet. Click "Add Partner" to create one.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="community-partners-grid">
                    {(content.community_partners || []).map((p, idx) => (
                      <div key={idx} className="rounded-lg border border-border bg-[#1a1a1a] p-3 space-y-2" data-testid={`community-partner-row-${idx}`}>
                        <div className="relative h-24 rounded bg-black/40 flex items-center justify-center overflow-hidden">
                          {p.logo ? (
                            <img src={mediaUrl(p.logo)} alt={p.name || `partner ${idx + 1}`} className="max-h-24 max-w-full object-contain p-2" />
                          ) : (
                            <ImageIcon className="h-8 w-8 text-[#3f3f46]" />
                          )}
                        </div>
                        <Button
                          data-testid={`community-partner-${idx}-upload`}
                          onClick={() => pickPartnerLogo(idx)}
                          disabled={uploadingPartner === idx}
                          variant="outline"
                          className="w-full rounded-full border-border text-xs h-8"
                        >
                          {uploadingPartner === idx ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />} {p.logo ? "Replace Logo" : "Upload Logo"}
                        </Button>
                        <Input
                          data-testid={`community-partner-${idx}-name`}
                          value={p.name || ""}
                          placeholder="Partner name"
                          onChange={(e) => updateCommunityPartner(idx, { name: e.target.value })}
                          onBlur={() => saveCommunityPartners()}
                          className="h-8 text-sm"
                        />
                        <Button
                          data-testid={`community-partner-${idx}-delete`}
                          onClick={() => removeCommunityPartner(idx)}
                          variant="ghost"
                          className="w-full h-8 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Panel>
          </TabsContent>

          {/* DAY PASS */}
          <TabsContent value="day">
            <Panel title="Day Pass Configuration" onSave={() => save()} saving={saving}>
              <ToggleRow
                testid="day-disabled-toggle"
                label="Day Pass Availability"
                help="Turn off to mark the Day Pass as Sold Out — the button is disabled on the landing page."
                checked={!content.day_pass.disabled}
                onChange={(v) => set((c) => ({ day_pass: { ...c.day_pass, disabled: !v } }))}
                onLabel="Available"
                offLabel="Sold Out"
              />
              <div className="mt-3">
                <TextField label="Sold Out Message" testid="day-soldout-message" value={content.day_pass.sold_out_message || ""} onChange={(v) => set((c) => ({ day_pass: { ...c.day_pass, sold_out_message: v } }))} />
              </div>
              <ListEditor
                label="What You Get"
                testid="day-wyg"
                items={content.day_pass.what_you_get || []}
                onChange={(arr) => set((c) => ({ day_pass: { ...c.day_pass, what_you_get: arr } }))}
              />
              <PassPricingEditor
                testid="day-pricing"
                pricing={content.day_pass.pricing || { price_per_person: 0, pass_types: [] }}
                onChange={(p) => set((c) => ({ day_pass: { ...c.day_pass, pricing: p } }))}
              />
              <PriceListEditor
                label="T-shirt Sizes"
                testid="day-tshirt"
                nameKey="size"
                items={content.day_pass.tshirt_sizes || []}
                onChange={(arr) => set((c) => ({ day_pass: { ...c.day_pass, tshirt_sizes: arr } }))}
              />

              {/* Bike Competition */}
              <div className="mt-8 rounded-xl border border-border p-5 bg-[#0F0F0F]">
                <div className="flex items-center gap-2 mb-1">
                  <Trophy className="h-5 w-5 text-[#FF4500]" />
                  <h3 className="font-display text-xl font-700 uppercase tracking-tight">Show Your Bike Competition</h3>
                </div>
                <p className="text-xs text-[#71717A] mb-4">Applies to both Day Pass & Meraki Pass forms. Used: <span className="text-[#E4E4E7]">{content.competition_entries_used ?? 0}</span>{content.competition_full ? " · FULL" : ""}</p>
                <ToggleRow
                  testid="comp-enabled-toggle"
                  label="Enable Competition Option"
                  help="Show the Yes/No option on both registration forms."
                  checked={!!content.bike_competition?.enabled}
                  onChange={(v) => set((c) => ({ bike_competition: { ...c.bike_competition, enabled: v } }))}
                  onLabel="Enabled"
                  offLabel="Disabled"
                />
                <div className="grid md:grid-cols-3 gap-4 mt-4">
                  <TextField label="Entry Fee (₹)" testid="comp-fee" type="number" value={content.bike_competition?.entry_fee ?? 0} onChange={(v) => set((c) => ({ bike_competition: { ...c.bike_competition, entry_fee: Number(v) || 0 } }))} />
                  <TextField label="Entry Count Limit (0 = unlimited)" testid="comp-limit" type="number" value={content.bike_competition?.entry_limit ?? 0} onChange={(v) => set((c) => ({ bike_competition: { ...c.bike_competition, entry_limit: Number(v) || 0 } }))} />
                  <TextField label="Description Box Label" testid="comp-label" value={content.bike_competition?.description_label || ""} onChange={(v) => set((c) => ({ bike_competition: { ...c.bike_competition, description_label: v } }))} />
                </div>
              </div>
            </Panel>
          </TabsContent>

          {/* MERAKI PASS */}
          <TabsContent value="meraki">
            <Panel title="Meraki Pass Configuration" onSave={() => save()} saving={saving}>
              <ToggleRow
                testid="meraki-disabled-toggle"
                label="Meraki Pass Availability"
                help="Turn off to mark the Meraki Pass as Sold Out — the button is disabled on the landing page."
                checked={!content.meraki_pass.disabled}
                onChange={(v) => set((c) => ({ meraki_pass: { ...c.meraki_pass, disabled: !v } }))}
                onLabel="Available"
                offLabel="Sold Out"
              />
              <div className="mt-3 mb-2">
                <TextField label="Sold Out Message" testid="meraki-soldout-message" value={content.meraki_pass.sold_out_message || ""} onChange={(v) => set((c) => ({ meraki_pass: { ...c.meraki_pass, sold_out_message: v } }))} />
              </div>
              <ListEditor
                label="What You Get"
                testid="meraki-wyg"
                items={content.meraki_pass.what_you_get || []}
                onChange={(arr) => set((c) => ({ meraki_pass: { ...c.meraki_pass, what_you_get: arr } }))}
              />
              <PassPricingEditor
                testid="meraki-pricing"
                pricing={content.meraki_pass.pricing || { price_per_person: 0, pass_types: [] }}
                onChange={(p) => set((c) => ({ meraki_pass: { ...c.meraki_pass, pricing: p } }))}
              />
              <PriceListEditor
                label="T-shirt Sizes"
                testid="meraki-tshirt"
                nameKey="size"
                items={content.meraki_pass.tshirt_sizes || []}
                onChange={(arr) => set((c) => ({ meraki_pass: { ...c.meraki_pass, tshirt_sizes: arr } }))}
              />
              <div className="grid md:grid-cols-2 gap-6 mt-2">
                <ListEditor label="Food Options" testid="meraki-food" items={content.meraki_pass.food_options || []} onChange={(arr) => set((c) => ({ meraki_pass: { ...c.meraki_pass, food_options: arr } }))} />
                <ListEditor label="Drinks Options" testid="meraki-drinks" items={content.meraki_pass.drinks_options || []} onChange={(arr) => set((c) => ({ meraki_pass: { ...c.meraki_pass, drinks_options: arr } }))} />
              </div>
            </Panel>
          </TabsContent>

          {/* VEHICLES */}
          <TabsContent value="vehicles">
            <Panel title="Vehicles (Bikes & Cars)" onSave={() => save()} saving={saving}>
              <p className="text-sm text-[#71717A] mb-2">These brand/model lists power the vehicle dropdowns on both registration forms.</p>
              <h3 className="font-display text-lg font-700 uppercase tracking-tight mt-4">Bike Brands</h3>
              <BrandEditor testPrefix="bike-brand" brands={content.vehicles?.bike || []} onChange={(arr) => set((c) => ({ vehicles: { ...c.vehicles, bike: arr } }))} />
              <h3 className="font-display text-lg font-700 uppercase tracking-tight mt-8">Car Brands</h3>
              <BrandEditor testPrefix="car-brand" brands={content.vehicles?.car || []} onChange={(arr) => set((c) => ({ vehicles: { ...c.vehicles, car: arr } }))} />
            </Panel>
          </TabsContent>

          {/* GENERAL */}
          <TabsContent value="general">
            <Panel title="General Settings" onSave={() => save()} saving={saving}>
              <div className="grid md:grid-cols-2 gap-4 max-w-xl">
                <TextField label="Day Pass Button Label" testid="btn-day-label" value={content.buttons.day_pass_label} onChange={(v) => set((c) => ({ buttons: { ...c.buttons, day_pass_label: v } }))} />
                <TextField label="Meraki Pass Button Label" testid="btn-meraki-label" value={content.buttons.meraki_pass_label} onChange={(v) => set((c) => ({ buttons: { ...c.buttons, meraki_pass_label: v } }))} />
                <TextField label="Sponsor Button Label" testid="btn-sponsor-label" value={content.buttons.sponsor_label || ""} onChange={(v) => set((c) => ({ buttons: { ...c.buttons, sponsor_label: v } }))} />
              </div>
              <div className="mt-5 max-w-xl">
                <ToggleRow
                  testid="btn-sponsor-toggle"
                  label="Show 'Be Our Sponsor' Button"
                  help="Turn off to hide the sponsor button from the landing page entirely."
                  checked={content.buttons.sponsor_enabled !== false}
                  onChange={(v) => set((c) => ({ buttons: { ...c.buttons, sponsor_enabled: v } }))}
                  onLabel="Shown"
                  offLabel="Hidden"
                />
              </div>
            </Panel>
          </TabsContent>

          {/* REGISTRATIONS */}
          <TabsContent value="regs">
            <RegistrationsTab regs={regs} reload={loadAll} onExport={exportCsv} />
          </TabsContent>

          <TabsContent value="coupons">
            <CouponsTab content={content} />
          </TabsContent>

          <TabsContent value="activities">
            <ActivitiesTab />
          </TabsContent>

          {/* SPONSORS */}
          <TabsContent value="sponsors">
            <Panel title={`Sponsor Enquiries (${sponsors.length})`} extra={
              <Button data-testid="export-sponsors-csv-button" onClick={() => exportSponsorsCsv(sponsors)} disabled={sponsors.length === 0} className="rounded-full bg-[#FF4500] hover:bg-[#FF5722]">
                <Download className="h-4 w-4 mr-1" /> Export CSV
              </Button>
            }>
              <div className="overflow-x-auto rounded-lg border border-border">
                <Table data-testid="sponsors-table">
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead>Name</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Received</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sponsors.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-[#71717A] py-8">No sponsor enquiries yet</TableCell></TableRow>
                    )}
                    {sponsors.map((s) => (
                      <TableRow key={s.id} className="border-border" data-testid={`sponsor-row-${s.id}`}>
                        <TableCell className="font-medium">{s.name}</TableCell>
                        <TableCell><span className="inline-flex items-center gap-1.5"><Handshake className="h-3.5 w-3.5 text-[#FF4500]" />{s.company_name}</span></TableCell>
                        <TableCell className="text-[#A1A1AA]">{s.mobile}</TableCell>
                        <TableCell className="text-[#A1A1AA]">{s.email}</TableCell>
                        <TableCell className="text-[#71717A] text-xs">{new Date(s.created_at).toLocaleString("en-IN")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Panel>
          </TabsContent>

          {/* WHATSAPP */}
          <TabsContent value="whatsapp">
            <WhatsAppConfig />
          </TabsContent>

          {/* PAYMENTS */}
          <TabsContent value="payments">
            <RazorpayConfig />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-border bg-[#121212] p-5">
      <Icon className="h-5 w-5 text-[#FF4500] mb-3" />
      <div className="font-display text-2xl font-800">{value}</div>
      <div className="text-xs uppercase tracking-wide text-[#71717A] mt-1">{label}</div>
    </div>
  );
}

function Panel({ title, children, onSave, saving, extra }) {
  return (
    <div className="rounded-2xl border border-border bg-[#121212] p-6 mt-4">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl font-800 uppercase tracking-tight">{title}</h2>
        {extra}
        {onSave && (
          <Button data-testid="save-button" onClick={onSave} disabled={saving} className="rounded-full bg-[#FF4500] hover:bg-[#FF5722]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save Changes
          </Button>
        )}
      </div>
      {children}
    </div>
  );
}

function exportSponsorsCsv(sponsors) {
  const cols = [["name", "Name"], ["company_name", "Company"], ["mobile", "Mobile"], ["email", "Email"], ["created_at", "Received At"]];
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [cols.map((c) => c[1]).join(","), ...sponsors.map((r) => cols.map((c) => esc(r[c[0]])).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `meraki-sponsors-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function exportCsv(regs) {
  const cols = [
    ["name", "Name"], ["pass_type", "Pass"], ["pass_tier", "Tier"], ["pass_count", "Passes"],
    ["people_total", "Attendees"], ["mobile", "Mobile"], ["email", "Email"],
    ["food_preference", "Food"], ["drinks_preference", "Drinks"],
    ["amount", "Amount (INR)"], ["status", "Status"], ["created_at", "Created At"],
  ];
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const memberSummary = (r) => (Array.isArray(r.members) ? r.members : []).map((m, i) =>
    `#${i + 1} ${m.name || ""} (${m.mobile || ""}) ${m.vehicle_type || ""}:${m.vehicle_brand || ""} ${m.vehicle_model || ""} | Tee:${m.tshirt_size || "-"} | Comp:${m.competition_entry ? "Yes" : "No"}`
  ).join(" ; ");
  const compCount = (r) => (Array.isArray(r.members) ? r.members.filter((m) => m.competition_entry).length : 0);
  const activitySummary = (r) => (Array.isArray(r.activities) ? r.activities : []).map((a) =>
    `${a.name || ""} x${a.qty || 0} (₹${a.subtotal != null ? a.subtotal : ""})`
  ).join(" ; ");
  const header = [...cols.map((c) => c[1]), "Competition Entries", "Activities", "Attendee Details"].join(",");
  const rows = regs.map((r) => [...cols.map((c) => esc(r[c[0]])), esc(compCount(r)), esc(activitySummary(r)), esc(memberSummary(r))].join(","));
  const csv = [header, ...rows].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `meraki-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function ToggleRow({ label, help, checked, onChange, testid, onLabel = "On", offLabel = "Off" }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-[#0A0A0A] p-4">
      <div>
        <div className="text-sm font-semibold text-white">{label}</div>
        {help && <div className="text-xs text-[#71717A] mt-0.5 max-w-md">{help}</div>}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className={`text-xs font-semibold uppercase ${checked ? "text-[#25D366]" : "text-[#FF7043]"}`}>{checked ? onLabel : offLabel}</span>
        <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
      </div>
    </div>
  );
}


function PassPricingEditor({ pricing, onChange, testid }) {
  const setPrice = (v) => onChange({ ...pricing, price_per_person: Number(v) || 0 });
  const setType = (i, field, v) => {
    const numeric = ["people_per_pass", "discount_percent", "max_passes", "min_members", "max_members"].includes(field);
    const types = pricing.pass_types.map((t, idx) => (idx === i ? { ...t, [field]: numeric ? (Number(v) || 0) : v } : t));
    onChange({ ...pricing, pass_types: types });
  };
  return (
    <div className="mt-5 rounded-xl border border-border p-5 bg-[#0F0F0F]">
      <h3 className="font-display text-xl font-700 uppercase tracking-tight mb-3">Pricing & Pass Types</h3>
      <div className="max-w-xs mb-4">
        <TextField label="Price Per Person (₹)" testid={`${testid}-ppp`} type="number" value={pricing.price_per_person} onChange={setPrice} />
      </div>
      <div className="space-y-3">
        {pricing.pass_types?.map((t, i) => (
          <div key={t.key} className="rounded-lg border border-border p-3" data-testid={`${testid}-type-${t.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-semibold text-sm capitalize">{t.label || t.key}</span>
              {t.is_group && <span className="text-[10px] font-bold text-[#FF7043] bg-[#FF4500]/15 rounded-full px-2 py-0.5">GROUP</span>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <TextField label="Label" testid={`${testid}-${t.key}-label`} value={t.label || ""} onChange={(v) => setType(i, "label", v)} />
              {!t.is_group && <TextField label="People / Pass" testid={`${testid}-${t.key}-people`} type="number" value={t.people_per_pass} onChange={(v) => setType(i, "people_per_pass", v)} />}
              <TextField label="Discount %" testid={`${testid}-${t.key}-discount`} type="number" value={t.discount_percent} onChange={(v) => setType(i, "discount_percent", v)} />
              <TextField label="Max Passes" testid={`${testid}-${t.key}-maxpasses`} type="number" value={t.max_passes} onChange={(v) => setType(i, "max_passes", v)} />
              {t.is_group && <TextField label="Min Members" testid={`${testid}-${t.key}-min`} type="number" value={t.min_members} onChange={(v) => setType(i, "min_members", v)} />}
              <TextField label={t.is_group ? "Max Members" : "Max Attendees"} testid={`${testid}-${t.key}-max`} type="number" value={t.max_members} onChange={(v) => setType(i, "max_members", v)} />
            </div>
            {!t.is_group && (
              <p className="mt-2 text-[11px] text-[#71717A]">
                Set <b>Max Attendees</b> higher than <b>People / Pass</b> to let buyers choose the number of attendees (1–{t.max_members || 1}) and fill details for each.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, testid, type = "text", className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">{label}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function ListEditor({ label, items, onChange, testid }) {
  const update = (i, v) => onChange(items.map((x, idx) => (idx === i ? v : x)));
  return (
    <div className="mt-5">
      <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">{label}</Label>
      <div className="space-y-2 mt-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <Input data-testid={`${testid}-item-${i}`} value={it} onChange={(e) => update(i, e.target.value)} />
            <Button data-testid={`${testid}-remove-${i}`} onClick={() => onChange(items.filter((_, idx) => idx !== i))} variant="outline" size="icon" className="border-border shrink-0"><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button data-testid={`${testid}-add`} onClick={() => onChange([...items, ""])} variant="outline" size="sm" className="rounded-full border-border"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
    </div>
  );
}

function PriceListEditor({ label, items, onChange, nameKey, testid }) {
  const update = (i, field, v) => onChange(items.map((x, idx) => (idx === i ? { ...x, [field]: field === "price" ? Number(v) || 0 : v } : x)));
  return (
    <div className="mt-5">
      <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">{label}</Label>
      <div className="space-y-2 mt-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <Input data-testid={`${testid}-name-${i}`} className="flex-1" value={it[nameKey]} onChange={(e) => update(i, nameKey, e.target.value)} placeholder="Name" />
            <Input data-testid={`${testid}-price-${i}`} className="w-36" type="number" value={it.price} onChange={(e) => update(i, "price", e.target.value)} placeholder="Price" />
            <Button data-testid={`${testid}-remove-${i}`} onClick={() => onChange(items.filter((_, idx) => idx !== i))} variant="outline" size="icon" className="border-border shrink-0"><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button data-testid={`${testid}-add`} onClick={() => onChange([...items, { [nameKey]: "", price: 0 }])} variant="outline" size="sm" className="rounded-full border-border"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
    </div>
  );
}

function BrandEditor({ brands, onChange, testPrefix = "brand" }) {
  const updateBrand = (i, field, v) => onChange(brands.map((b, idx) => (idx === i ? { ...b, [field]: v } : b)));
  const commitModels = (i, text) => updateBrand(i, "models", text.split(",").map((s) => s.trim()).filter(Boolean));
  return (
    <div className="mt-5">
      <Label className="text-xs uppercase tracking-wide text-[#A1A1AA]">Brands & Models</Label>
      <p className="text-xs text-[#71717A] mt-1 mb-2">Enter models separated by commas.</p>
      <div className="space-y-3">
        {brands.map((b, i) => (
          <div key={i} className="rounded-lg border border-border p-3 space-y-2">
            <div className="flex gap-2 items-center">
              <Input data-testid={`${testPrefix}-name-${i}`} className="max-w-xs" value={b.brand} onChange={(e) => updateBrand(i, "brand", e.target.value)} placeholder="Brand" />
              <Button data-testid={`${testPrefix}-remove-${i}`} onClick={() => onChange(brands.filter((_, idx) => idx !== i))} variant="outline" size="icon" className="border-border shrink-0"><Trash2 className="h-4 w-4" /></Button>
            </div>
            <ModelsInput
              testid={`${testPrefix}-models-${i}`}
              value={(b.models || []).join(", ")}
              onCommit={(text) => commitModels(i, text)}
            />
          </div>
        ))}
        <Button data-testid={`${testPrefix}-add`} onClick={() => onChange([...brands, { brand: "", models: [] }])} variant="outline" size="sm" className="rounded-full border-border"><Plus className="h-4 w-4 mr-1" /> Add Brand</Button>
      </div>
    </div>
  );
}

function ModelsInput({ value, onCommit, testid }) {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  return (
    <Textarea
      data-testid={testid}
      rows={2}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onCommit(text)}
      placeholder="Model A, Model B"
    />
  );
}
